import { useState } from 'react';
import { X, Ruler, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';

const SizeGuide = ({ isOpen, onClose, category }) => {
  // Yeezy size chart - exact measurements as specified
  const yeezyData = [
    { eu: '36', cm: '22.1' },
    { eu: '36 2/3', cm: '22.5' },
    { eu: '37 1/3', cm: '22.9' },
    { eu: '38', cm: '23.3' },
    { eu: '38 2/3', cm: '23.8' },
    { eu: '39 1/3', cm: '24.2' },
    { eu: '40', cm: '24.6' },
    { eu: '40 2/3', cm: '25.0' },
    { eu: '41 1/3', cm: '25.5' },
    { eu: '42', cm: '25.9' },
    // Note: 42 2/3 is intentionally excluded
    { eu: '43 1/3', cm: '26.7' },
    { eu: '44', cm: '27.1' },
    { eu: '44 2/3', cm: '27.6' },
    { eu: '45 1/3', cm: '28.0' },
    { eu: '46', cm: '28.4' },
    { eu: '46 2/3', cm: '28.8' },
    { eu: '47 1/3', cm: '29.3' },
  ];

  // Check if product is Yeezy-related
  const isYeezy = category?.toLowerCase().includes('yeezy');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-lg md:w-full bg-[#121212] border border-white/10 z-50 overflow-y-auto max-h-[90vh]"
            data-testid="size-guide-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Ruler className="w-5 h-5 text-[#c00000]" />
                <h2 className="font-heading font-bold text-lg text-white tracking-wider">
                  GHID MARIMI {isYeezy ? 'YEEZY' : ''}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-white/50 hover:text-white transition-colors p-1"
                data-testid="close-size-guide"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Important Notice */}
              <div className="bg-[#c00000]/10 border border-[#c00000]/30 p-4 mb-6">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-[#c00000] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading text-sm text-[#c00000] mb-1">IMPORTANT</p>
                    <p className="font-body text-sm text-white/70 leading-relaxed">
                      Masuratoarea reprezinta <strong className="text-white">LUNGIMEA PICIORULUI</strong>, masurata de la calca pana la varful celui mai lung deget, NU lungimea fizica a pantofului sau a talpii interioare.
                    </p>
                  </div>
                </div>
              </div>

              {/* Size Table */}
              <div className="mb-6">
                <h3 className="font-heading text-sm text-white/50 uppercase tracking-wider mb-4">
                  Tabel de marimi {isYeezy ? 'Yeezy' : ''}
                </h3>
                <div className="border border-white/10 overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-2 bg-[#0a0a0a]">
                    <div className="px-4 py-3 border-r border-white/10">
                      <span className="font-heading text-xs text-white/50 uppercase tracking-wider">Marime EU</span>
                    </div>
                    <div className="px-4 py-3">
                      <span className="font-heading text-xs text-white/50 uppercase tracking-wider">Lungime Picior</span>
                    </div>
                  </div>
                  {/* Table Body */}
                  <div className="divide-y divide-white/5">
                    {yeezyData.map((row, idx) => (
                      <div 
                        key={idx} 
                        className={`grid grid-cols-2 ${idx % 2 === 0 ? 'bg-[#121212]' : 'bg-[#0a0a0a]'}`}
                      >
                        <div className="px-4 py-3 border-r border-white/10">
                          <span className="font-heading text-sm text-white">{row.eu}</span>
                        </div>
                        <div className="px-4 py-3">
                          <span className="font-body text-sm text-white">{row.cm} cm</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Measuring Instructions */}
              <div className="bg-[#0a0a0a] p-4 mb-6">
                <h3 className="font-heading text-sm text-white mb-3 uppercase tracking-wider">
                  Cum iti masori piciorul
                </h3>
                <ol className="font-body text-sm text-white/70 space-y-3">
                  <li className="flex gap-3">
                    <span className="font-heading text-[#c00000]">1.</span>
                    <span>Pune-ti piciorul pe o foaie de hartie, cu calcaiul lipit de perete.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-heading text-[#c00000]">2.</span>
                    <span>Marcheaza varful celui mai lung deget pe hartie.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-heading text-[#c00000]">3.</span>
                    <span>Masoara distanta de la perete pana la marcaj.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-heading text-[#c00000]">4.</span>
                    <span>Masoara ambele picioare si foloseste masuratoarea mai mare.</span>
                  </li>
                </ol>
              </div>

              {/* Between Sizes Tip */}
              <div className="bg-[#1a1a1a] border border-white/10 p-4">
                <p className="font-body text-sm text-white/70 leading-relaxed">
                  <strong className="text-white">Sfat:</strong> Daca te afli intre doua marimi, recomandam sa alegi marimea mai mare pentru un confort sporit.
                </p>
              </div>

              {/* Close Button */}
              <Button
                onClick={onClose}
                className="w-full mt-6 bg-[#c00000] text-white hover:bg-[#a00000] rounded-none h-12 font-heading tracking-wider"
              >
                AM INTELES
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SizeGuide;
